import { useMemo } from "react";
import { useLocation } from "react-router-dom";

export const DASHBOARD_TAB_VALUES = Object.freeze([
  "assignments",
  "statics",
  "map",
  "users",
  "devices",
  "alarms",
  "events",
  "live_camera",
  "task_management",
  "my_tasks",
]);

const DEFAULT_DASHBOARD_TAB = "statics";
const DASHBOARD_NAV_STORAGE_PREFIX = "gps:dashboard-nav:";
export const LIVE_MAP_PATH = "/live-map";

export const normalizeDashboardTab = (value, fallback = DEFAULT_DASHBOARD_TAB) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return DASHBOARD_TAB_VALUES.includes(normalized) ? normalized : fallback;
};

export const readDashboardTabFromSearch = (search, fallback = DEFAULT_DASHBOARD_TAB) => {
  const params = new URLSearchParams(String(search || ""));
  return normalizeDashboardTab(params.get("tab"), fallback);
};

export const buildAdminDashboardPath = ({
  tab,
  mapStatusFilter = null,
  navToken = null,
} = {}) => {
  const params = new URLSearchParams();
  params.set("tab", normalizeDashboardTab(tab));

  if (mapStatusFilter) {
    params.set(
      "status",
      String(mapStatusFilter || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_")
    );
  }

  if (navToken) params.set("nav", String(navToken));

  const query = params.toString();
  return `/dashboard${query ? `?${query}` : ""}`;
};

export const buildLiveMapPath = ({
  mapStatusFilter = null,
  navToken = null,
} = {}) => {
  const params = new URLSearchParams();

  if (mapStatusFilter) {
    params.set(
      "status",
      String(mapStatusFilter || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_")
    );
  }

  if (navToken) params.set("nav", String(navToken));

  const query = params.toString();
  return `${LIVE_MAP_PATH}${query ? `?${query}` : ""}`;
};

function makeDashboardNavToken() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export const stashDashboardNavPayload = (payload = {}) => {
  const normalizedPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
  );
  if (!Object.keys(normalizedPayload).length) return null;

  const token = makeDashboardNavToken();
  try {
    sessionStorage.setItem(
      `${DASHBOARD_NAV_STORAGE_PREFIX}${token}`,
      JSON.stringify({
        createdAt: Date.now(),
        payload: normalizedPayload,
      })
    );
    return token;
  } catch (_) {
    return null;
  }
};

export const consumeDashboardNavPayload = (token) => {
  const key = `${DASHBOARD_NAV_STORAGE_PREFIX}${String(token || "").trim()}`;
  if (!String(token || "").trim()) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed?.payload && typeof parsed.payload === "object" ? parsed.payload : null;
  } catch (_) {
    sessionStorage.removeItem(key);
    return null;
  }
};

export const navigateToAdminDashboard = (
  navigate,
  {
    tab = DEFAULT_DASHBOARD_TAB,
    mapStatusFilter = null,
    quickAction = null,
    focusRoute = null,
    playbackFocus = null,
    replace = false,
  } = {}
) => {
  const navPayload = {};
  if (quickAction) navPayload.quickAction = quickAction;
  if (focusRoute) navPayload.focusRoute = focusRoute;
  if (playbackFocus) navPayload.playbackFocus = playbackFocus;
  const navToken = stashDashboardNavPayload(navPayload);
  const normalizedTab = normalizeDashboardTab(tab);

  if (normalizedTab === "map") {
    navigate(
      buildLiveMapPath({
        mapStatusFilter,
        navToken,
      }),
      { replace }
    );
    return;
  }

  navigate(
    buildAdminDashboardPath({
      tab: normalizedTab,
      mapStatusFilter,
      navToken,
    }),
    { replace }
  );
};

export const useResolvedDashboardTab = (fallback = DEFAULT_DASHBOARD_TAB) => {
  const location = useLocation();
  return useMemo(() => {
    if (location.pathname !== "/dashboard") return fallback;
    return readDashboardTabFromSearch(location.search, fallback);
  }, [fallback, location.pathname, location.search]);
};
